import { api } from "@/shared/api";
import type { AugmentTask } from "@/shared/api/augment";

// FSD entities/character — 人物库(packs W2)。
// 契约锚点:gscq-api PackCharacterController(base /api/v1/characters,W1 地基)。
// 库墙/详情/物料是团队共用创作资产(租户三角色全员可见);真人形象与声纹授权入库后全公司共用
// (录音克隆须本人、跨公司隔离、删除仅本人或管理员)。

/** 物料槽(后端枚举定死;场景不设槽 —— 拍摄语境收敛到视频创建时输入,2026-07-27 拍板)。 */
export type CharacterSlot = "identity" | "expression" | "pose" | "outfit" | "dh_source";

/**
 * 「真人出镜身份素材」区的四槽(定妆/表情/姿态/造型)。★不含 dh_source:
 * 这份表同时是 AI 补足的槽面、详情页「改槽」下拉、真人出镜素材排序的口径 ——
 * 分身口播素材不进这些地方。分身素材槽单列 {@link DH_SOURCE_SLOT}。
 */
export const CHARACTER_SLOTS: CharacterSlot[] = ["identity", "expression", "pose", "outfit"];

/**
 * 分身口播素材槽(1006):分身仓上传的视频/照片落这里,与真人出镜身份素材分开 ——
 * 分身仓建的人物不再混进真人出镜选人。
 */
export const DH_SOURCE_SLOT = "dh_source" as const satisfies CharacterSlot;

/** 槽中文名 + 一句话说明(对齐设计 §2.2 版图口径)。 */
export const SLOT_META: Record<CharacterSlot, { label: string; hint: string }> = {
  identity: { label: "定妆", hint: "正面(主)/45°/侧面/背面 —— 视频首帧锚、海报人物" },
  expression: { label: "表情", hint: "微笑/说话/惊喜/沉思/皱眉 —— 分镜参考" },
  pose: { label: "姿态", hint: "手持/指向/口播/行走/挥手 —— 开箱·导览·喊话" },
  outfit: { label: "造型", hint: "商务/休闲/工装/节庆 —— 品牌宣言·公司的一天" },
  dh_source: { label: "分身口播素材", hint: "分身仓上传的口播视频/照片 —— 只给分身出片用" },
};

/**
 * 物料视图。`objectKey` 下发(消费端拿它当锚 ref 落库,与产品图口径一致 —— 落库永远是 key);
 * `url` = 现签预览(会过期,仅展示这一次,不要缓存进状态复用)。
 */
export interface CharacterMaterialDto {
  id: number;
  slot: CharacterSlot;
  label?: string | null;
  objectKey: string;
  /** upload(人传)/ ai(AI 补足,W3)。 */
  source?: string | null;
  sortWeight?: number | null;
  brandProtect?: boolean;
  url?: string | null;
  /** 1280 长边缩略图现签。存量物料回填前为 null —— 消费面必须 `thumbUrl ?? url` 回退。 */
  thumbUrl?: string | null;
  /**
   * 素材介质(0915 归一批 B):image | video。
   * ★消费面必须按它决定用 `<img>` 还是 `<video>` 渲染 —— 视频素材的 thumbUrl 为 null
   * (没有可直接缩略的位图),当成图渲染会得到一个裂图。
   */
  kind?: "image" | "video" | null;
  /** 视频时长秒(ai 侧 ffprobe 回填);image 恒 null。报价按它算 vin 族 qty。 */
  durationSec?: number | null;
}

/**
 * 真人认证状态(SEEDANCE 真人出镜 P0,0914 车 D · §2.1 契约,逐字):
 * `status ∈ not_required | pending | verified | failed`。虚拟人物恒
 * `not_required` + `required:false`。`canStart` 是 P1 「去认证」按钮的可用性信号
 * (H5 扫码通道用;P0 exempt 档下恒 false,本批不消费)。
 *
 * `assets`(0914 追加,契约补齐):素材(定妆图/视频)入库到 vapeur 的状态,与 `status`
 * 是**两把尺**——`status` 答「这个人认证过没有」,`assets` 答「这个人的素材有没有
 * 入库完成、能不能真的送进真人线」。两者都要放行才能真人出镜:P0 exempt 档下
 * `status` 恒 `not_required`(白过),真正卡门槛的是 `assets` 有没有到 `active`。
 * `assets` 允许 undefined(向后兼容态,按 `"none"` 处理——未入库,不当"免检")。
 */
export interface CharacterVerificationDto {
  required: boolean;
  status: "not_required" | "pending" | "verified" | "failed";
  canStart: boolean;
  assets?: "none" | "registering" | "active" | "failed" | "rejected";
}

/**
 * 人物视图。`appearance` = 唯一进提示词链的人物文本(0916 角色卡 personaJson 整块退役:
 * 12 字段零消费面、零填写率);`primaryUrl` = 主素材现签(库墙卡面),`primaryKind` 说它是图还是视频;
 * `materials` 仅详情端点带,列表走子资源。
 */
export interface CharacterDto {
  id: number;
  name: string;
  appearance?: string | null;
  primaryMaterialId?: number | null;
  primaryUrl?: string | null;
  /** 主定妆图缩略图现签;存量未回填为 null —— 消费面回退 primaryUrl。 */
  primaryThumbUrl?: string | null;
  /**
   * 主素材介质(0916)。归一后「只有视频」的人物把视频钉成主素材,而视频没有缩略图 ——
   * 卡面必须按它分派 `<img>` / `<video>`,否则是一张裂图。
   * 列表端点不带 materials,这是 console 唯一能知道主素材是图还是视频的地方。
   */
  primaryKind?: "image" | "video" | null;
  voiceProfileId?: number | null;
  presetVoice?: string | null;
  isRealPerson: boolean;
  status: "active" | "disabled";
  materialCount?: number | null;
  materials?: CharacterMaterialDto[] | null;
  /**
   * ★向后兼容闸(0914,SEEDANCE 真人出镜 P0 车 D):这个字段**允许 undefined** ——
   * 后端老版本(还没接 V20 迁移/PackCharacterService 改造前)不下发它。undefined 时
   * 真人人物维持**旧行为 = 拦**(见 characterPick.ts 的 videoUsableCharacter /
   * realPersonBlockReason:统一按 `verification?.status` 缺席当"未验证"处理,不当
   * "免检"处理) —— 宁可旧后端下真人暂不可选,也不能因为字段缺席就放行一条没有
   * 认证兜底的真人出镜线。虚拟人物不受影响(判据入口就是 `isRealPerson` 分支)。
   */
  verification?: CharacterVerificationDto;
  /**
   * 该人物名下是否存在可用视频形象(P0.6 挂账车 L,0915)。服务端按显式挂接
   * (`avatar_profile.character_id`)优先、principal 兜底次之算好这个布尔,console 只管展示。
   * 允许 undefined(老后端未下发时按「没有」处理,不是新的确定性判断 —— 与 `verification`
   * 同一条向后兼容纪律)。虚拟人物恒 false/undefined。
   */
  hasVideoAvatar?: boolean;
  /**
   * 该人物身份视频的实测秒数(0915 车 E,vin 族报价修正方案 a)。仅在 `hasVideoAvatar`
   * 为真时有意义 —— 真人出镜「文案出片」对这类人物提交会带上这段身份参考视频,vapeur
   * 侧按 vin 计费码把它的 token 计进去(输入视频全量计量),报价 qty 要把这段秒数加上去
   * 才不亏本卖。api 侧 V24 落库,存量行可能是 `null`(未回填实测秒)—— 报价侧读到
   * `null`/`undefined` 一律回落 15 秒(vapeur 视频素材硬上限,宁可轻微高报也不漏计量),
   * 不当"没有身份视频"处理(那是 `hasVideoAvatar` 的职责,两个字段各管各的)。
   */
  identityVideoDurationSec?: number | null;
  /**
   * 分身可用的视频(1006):dh_source 槽或 identity 槽有视频。★老后端不下发 →
   * 回落 `hasVideoAvatar`(identity 有视频)。统一走 {@link characterHasDhVideo}。
   */
  hasDhVideo?: boolean;
  /**
   * 能不能进真人出镜选人(1006):后端按「有真人出镜身份素材」算,分身仓建的人物为 false。
   * ★老后端不下发 → 回落旧逻辑(不过滤),统一走 {@link realPersonEligibleOf}。
   */
  realPersonEligible?: boolean;
}

/** 分身「有视频可用」判据:hasDhVideo 优先,缺省回落 hasVideoAvatar。 */
export const characterHasDhVideo = (
  c: Pick<CharacterDto, "hasDhVideo" | "hasVideoAvatar">
): boolean => (typeof c.hasDhVideo === "boolean" ? c.hasDhVideo : c.hasVideoAvatar === true);

/**
 * 真人出镜选人过滤(1006):真人人物只认显式 `realPersonEligible === false` 才排除
 * (分身仓建的人物 = 只有 dh_source 素材);老后端缺省 → 旧逻辑,不过滤。
 * 虚拟人物不受这道筛子影响(分身仓只建真人;虚拟人的真人出镜可选性照旧)。
 * 后面的可选/置灰判据(认证、素材入库等)仍由 characterPick / realPersonPickState 各管各的。
 */
export const realPersonEligibleOf = (
  c: Pick<CharacterDto, "realPersonEligible" | "isRealPerson">
): boolean => !c.isRealPerson || c.realPersonEligible !== false;

/** 建/改请求。可清空字段语义与产品树同款:undefined=没提这件事别动,传了(哪怕空)=覆盖。 */
export interface CharacterUpsertRequest {
  name: string;
  appearance?: string;
  presetVoice?: string | null;
  voiceProfileId?: number | null;
  status?: "active" | "disabled";
  /**
   * 人物类型(0915 归一批 B):real | virtual,缺省 virtual。
   * ★采集流(真人)必须显式传 "real" —— 升格口退役后,不传就会建成虚拟人,
   *   而且**不报错**(只是静默走了另一类的规则:认证态/授权存证/真人照片闸全部错位)。
   */
  type?: "real" | "virtual";
}

/** 人脸预检结果。checked=false = 预检开关未启用(不是失败,当场放行,出片 gate 仍在链路里)。 */
export interface FacePrecheckResult {
  pass: boolean;
  reason?: string | null;
  checked: boolean;
}

/*
 * ★`FromAvatarProfileRequest` 与升格口一并退役(0915 归一批 B):形象表并入素材表后,
 * 「先 enroll 形象、再升格建人物」这条两步链的第一步没有了。采集流改为「建人物 + 传素材」
 * 两个既有端点 —— 顺带拔掉了"真人必须先有一张照片"这个前置(老板实报「只有视频他无法上传」
 * 就是被它挡住的:后端升格口对 kind=video 直接拒)。
 */

const BASE = "/characters";

export const characterService = {
  /** 库墙:全部人物(含 disabled;materialCount + 主定妆图现签,物料网格走子资源)。 */
  list: () => api.get<CharacterDto[]>(BASE),

  /** 详情(含 materials 全量)。 */
  get: (id: number) => api.get<CharacterDto>(`${BASE}/${id}`),

  create: (req: CharacterUpsertRequest) => api.post<CharacterDto>(BASE, req),

  update: (id: number, req: CharacterUpsertRequest) => api.put<CharacterDto>(`${BASE}/${id}`, req),

  /** 删人物(含级联物料)。 */
  remove: (id: number) => api.delete<void>(`${BASE}/${id}`),

  /** 钉主定妆图(必须是 identity 槽物料,后端校)。 */
  setPrimaryMaterial: (id: number, materialId: number) =>
    api.post<CharacterDto>(`${BASE}/${id}/primary-material`, { materialId }),

  // ── 物料子资源 ───────────────────────────────────────────────────────

  listMaterials: (id: number) => api.get<CharacterMaterialDto[]>(`${BASE}/${id}/materials`),

  /** 传物料(multipart file+slot+可选 label)。 */
  uploadMaterial: (
    id: number,
    file: File,
    slot: CharacterSlot,
    label?: string
  ): Promise<CharacterMaterialDto> => {
    const form = new FormData();
    form.append("file", file);
    form.append("slot", slot);
    if (label) form.append("label", label);
    return api.post<CharacterMaterialDto>(`${BASE}/${id}/materials`, form);
  },

  /** 改物料(slot 迁槽 / label;undefined=不动)。 */
  updateMaterial: (
    id: number,
    materialId: number,
    patch: { slot?: CharacterSlot; label?: string }
  ) => api.patch<CharacterMaterialDto>(`${BASE}/${id}/materials/${materialId}`, patch),

  deleteMaterial: (id: number, materialId: number) =>
    api.delete<void>(`${BASE}/${id}/materials/${materialId}`),

  /** slot 内重排:orderedIds 必须恰好覆盖该 slot 全部物料(后端全覆盖校验)。 */
  reorderMaterials: (id: number, slot: CharacterSlot, orderedIds: number[]) =>
    api.put<void>(`${BASE}/${id}/materials/order`, { slot, orderedIds }),

  // ── 老板 IP 流 ──────────────────────────────────────────────────────

  /**
   * 上传体检(人脸预检前置到入库:脸太小/太暗/多人当场报,不等出片 IMAGE_CHECK_FAILED)。
   * ★上游要求图 ≥640px,UI 提示「请传原图」。体检是咨询不是闸门,不落任何资产。
   */
  facePrecheck: (file: File): Promise<FacePrecheckResult> => {
    const form = new FormData();
    form.append("file", file);
    return api.post<FacePrecheckResult>(`${BASE}/face-precheck`, form);
  },

  /** 「我的形象」升格建真人人物(形象照复制为 identity 主定妆图)。 */

  // ── W5 AI 补足(批量生成 + 策展入库;产物暂存任务资产,挑中才入库)──────────

  /**
   * 起单:create(从零生成 3 选 1,虚拟人物专用)/ standardize(定妆标准化 3 选 1)/
   * slots(主定妆图按槽批量,空 slots=四槽全跑)。
   *
   * ★`brief` 只有 create 吃:那条路没有底图,"要画谁"只能由这句话说。真人人物 create 会被
   * 后端当场拒 —— 凭一句描述生成一个真实存在的人,正是肖像授权存证整条链要防的事。
   */
  augment: (
    id: number,
    req: { mode: string; slots?: string[]; anchorMaterialId?: number; brief?: string }
  ) => api.post<AugmentTask>(`${BASE}/${id}/augment`, req),

  /** 轮询口:状态 + 产物现签 + ai 预标 slot/label。 */
  augmentTask: (id: number, taskId: number) =>
    api.get<AugmentTask>(`${BASE}/${id}/augment-tasks/${taskId}`),

  /**
   * 最近 10 条补足任务(新→旧,不带产物)。
   *
   * ★0916 补挂账:面板一关,已生成的候选图就再也找不回来 —— taskId 只活在组件 state 里,
   * 而产物明明还在服务端。手滑关掉 = 那一单积分白烧。
   */
  augmentTasks: (id: number) => api.get<AugmentTask[]>(`${BASE}/${id}/augment-tasks`),

  /** 策展入库 N 选 M(后端复制到规范 pack key 再落库,source=generated)。 */
  curateFromTask: (
    id: number,
    taskId: number,
    picks: { assetId: number; slot: string; label?: string | null }[]
  ) => api.post<CharacterMaterialDto[]>(`${BASE}/${id}/materials/from-task`, { taskId, picks }),
};
